import { Ionicons } from '@expo/vector-icons';
import React from "react";
import { View, Text, StyleSheet, TouchableOpacity } from "react-native";

import { GameRoom } from "../../../features/quiz-core/multiplayerStore";
import { t } from "../../../i18n/translations";
import { iqesColors } from "../../../shared/ui/iqesDesignSystem";
import { useLanguageStore } from "../../quiz-core/languageStore";
import { resolveRoomGroupMeta } from "../utils/groupMeta";

interface LobbyItemProps {
  item: GameRoom;
  onJoin: () => void;
  onPreview: () => void;
  isHost?: boolean;
  groupTypeOverride?: 'family' | 'friends';
  groupNameOverride?: string;
}

const LobbyItem: React.FC<LobbyItemProps> = ({
  item,
  onJoin,
  onPreview,
  isHost = false,
  groupTypeOverride,
  groupNameOverride,
}) => {
  const { language } = useLanguageStore();

  const meta = resolveRoomGroupMeta(
    {
      ...item,
      room_type: groupTypeOverride || item.room_type,
      family_name:
        groupTypeOverride === 'family'
          ? groupNameOverride || item.family_name
          : item.family_name,
      friends_group_name:
        groupTypeOverride === 'friends'
          ? groupNameOverride || item.friends_group_name
          : item.friends_group_name,
    },
    language,
  );
  const roomTitle = meta.roomTitle;
  const roomVisibilityLabel = item.public
    ? t('multiplayer_lobby.public', language)
    : t('multiplayer_lobby.private', language);
  const roomStatusLabel =
    item.status === 'waiting'
      ? t('multiplayer_lobby.statusWaiting', language)
      : t('multiplayer_lobby.statusPlaying', language);
  const currentPlayers = item.current_players ?? 0;

  return (
    <View style={styles.card}>
      <View style={styles.headerContainer}>
        <View style={styles.titleCluster}>
          <View style={styles.titleIconWrap}>
            <Ionicons
              name="game-controller"
              size={18}
              color={iqesColors.goldBright}
              style={styles.titleIcon}
            />
          </View>
          <View style={styles.titleContent}>
            <Text style={styles.roomTitle}>
              {roomTitle}
            </Text>
            <Text style={styles.roomSubtitle}>
              {currentPlayers > 0
                ? t('multiplayer_lobby.playersInRoom', language).replace('{{count}}', String(currentPlayers))
                : t('multiplayer_lobby.readyToBegin', language)}
            </Text>
          </View>
        </View>

        <View style={styles.codeBadge}>
          <Text style={styles.codeBadgeLabel}>
            {t('multiplayer_lobby.gameRoomCode', language)}
          </Text>
          <Text style={styles.codeBadgeValue}>{item.code}</Text>
        </View>
      </View>

      <View style={styles.metaRow}>
        <View style={styles.metaChip}>
          <Ionicons
            name={item.public ? "globe-outline" : "lock-closed-outline"}
            size={14}
            color={item.public ? iqesColors.greenBright : iqesColors.goldBright}
            style={styles.metaChipIcon}
          />
          <Text style={styles.metaChipText}>
            {roomVisibilityLabel}
          </Text>
        </View>

        <View style={styles.metaChip}>
          <Ionicons
            name={item.status === 'waiting' ? 'time-outline' : 'play-circle-outline'}
            size={14}
            color={iqesColors.goldBright}
            style={styles.metaChipIcon}
          />
          <Text style={styles.metaChipText}>
            {roomStatusLabel}
          </Text>
        </View>

        {isHost && (
          <View style={styles.hostBadge}>
            <Ionicons name="trophy" size={14} color={iqesColors.goldBright} />
            <Text style={styles.hostBadgeText}>
              {t('multiplayer_lobby.yourRoom', language)}
            </Text>
          </View>
        )}
      </View>

      <View style={styles.buttonContainer}>
        <TouchableOpacity onPress={onPreview} style={[styles.button, styles.previewButton]} activeOpacity={0.85}>
          <Text style={styles.previewButtonText}>{t('multiplayer_lobby.previewPlayers', language)}</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={onJoin} style={[styles.button, styles.joinButton]} activeOpacity={0.9}>
          <Text style={styles.joinButtonText}>
            {isHost ? t('multiplayer_lobby.enterRoom', language) : t('multiplayer_lobby.joinRoom', language)}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    marginBottom: 16,
    borderRadius: 24,
    paddingHorizontal: 18,
    paddingVertical: 18,
    backgroundColor: 'rgba(8, 10, 24, 0.92)',
    borderWidth: 1,
    borderColor: 'rgba(246, 190, 58, 0.36)',
    shadowColor: iqesColors.gold,
    shadowOpacity: 0.14,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 8 },
    elevation: 7,
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 12,
    gap: 12,
  },
  titleCluster: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  titleIconWrap: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
    backgroundColor: 'rgba(246, 190, 58, 0.16)',
    borderWidth: 1,
    borderColor: 'rgba(246, 190, 58, 0.3)',
  },
  titleIcon: {
    marginRight: 0,
  },
  titleContent: {
    flex: 1,
  },
  roomTitle: {
    color: iqesColors.ivory,
    fontSize: 18,
    fontWeight: '800',
    marginBottom: 2,
  },
  roomSubtitle: {
    color: iqesColors.mutedWarm,
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 19,
  },
  codeBadge: {
    borderRadius: 18,
    paddingHorizontal: 12,
    paddingVertical: 10,
    minWidth: 86,
    alignItems: 'center',
    backgroundColor: 'rgba(255, 248, 232, 0.08)',
    borderWidth: 1,
    borderColor: 'rgba(246, 190, 58, 0.24)',
  },
  codeBadgeLabel: {
    color: iqesColors.mutedWarm,
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
    marginBottom: 2,
  },
  codeBadgeValue: {
    color: iqesColors.ivory,
    fontSize: 15,
    fontWeight: '800',
    letterSpacing: 0.8,
  },
  metaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
  },
  metaChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 999,
    borderWidth: 1,
    backgroundColor: 'rgba(255, 248, 232, 0.08)',
    borderColor: 'rgba(246, 190, 58, 0.25)',
  },
  metaChipIcon: {
    marginRight: 6,
  },
  metaChipText: {
    color: iqesColors.ivory,
    fontSize: 13,
    fontWeight: '700',
  },
  hostBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(246, 190, 58, 0.15)',
    borderWidth: 1,
    borderColor: 'rgba(246, 190, 58, 0.32)',
  },
  hostBadgeText: {
    color: iqesColors.goldSoft,
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 6,
  },
  buttonContainer: {
    gap: 10,
  },
  button: {
    minHeight: 48,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 0,
  },
  previewButton: {
    backgroundColor: 'rgba(255, 248, 232, 0.08)',
    borderWidth: 1,
    borderColor: 'rgba(246, 190, 58, 0.3)',
  },
  previewButtonText: {
    color: iqesColors.ivory,
    fontSize: 15,
    fontWeight: '800',
  },
  joinButton: {
    marginTop: 2,
    backgroundColor: iqesColors.royalPurple,
    shadowColor: iqesColors.gold,
    shadowOpacity: 0.18,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 7 },
    elevation: 5,
  },
  joinButtonText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '900',
  },
});

export default LobbyItem;
